import {
  classeDoAnimal,
  curvaDeReferencia,
  faixaDoPotro,
  idadeEmMeses,
  indices,
  type CurvaDeReferencia,
  type Classe,
  type FaixaDoPotro,
  type MedidasBrutas,
} from '@equiview/domain';
import { traitsV1, type Trait, type TraitGroup } from '@equiview/protocol';
import type { Animal, Avaliacao, Medida, Midia } from './fonte.js';

/** Rotulo, unidade e casas de cada medida vem do protocolo, nunca da tela. */
export const traitPorCodigo = new Map<string, Trait>(traitsV1.map((t) => [t.code, t]));

export const ROTULO_GRUPO: Record<TraitGroup, string> = {
  altura: 'Alturas',
  comprimento: 'Comprimentos',
  largura: 'Larguras',
  perimetro: 'Perímetros',
  angulo: 'Ângulos',
  aprumo_frente: 'Aprumos — frente',
  aprumo_perfil_anterior: 'Aprumos — perfil anterior',
  aprumo_perfil_posterior: 'Aprumos — perfil posterior',
  aprumo_tras: 'Aprumos — trás',
};

export const ORDEM_GRUPOS = Object.keys(ROTULO_GRUPO) as TraitGroup[];

export const ROTULO_CLASSE: Record<Classe, string> = {
  potro: 'Potro',
  garanhao: 'Garanhão',
  egua: 'Égua',
  castrado: 'Castrado',
};

export const ROTULO_CLASSE_PLURAL: Record<Classe, string> = {
  potro: 'Potros',
  garanhao: 'Garanhões',
  egua: 'Éguas',
  castrado: 'Castrados',
};

export const FAIXAS: FaixaDoPotro[] = ['0-12', '12-24', '24-36'];
export const ROTULO_FAIXA: Record<FaixaDoPotro, string> = {
  '0-12': '0–12 meses',
  '12-24': '12–24 meses',
  '24-36': '24–36 meses',
};

export const ROTULO_SEXO: Record<Animal['sex'], string> = {
  macho_inteiro: 'Macho inteiro',
  castrado: 'Castrado',
  femea: 'Fêmea',
};

export const ROTULO_ANDAMENTO = {
  passo: 'Passo',
  marcha_curta: 'Marcha curta',
  marcha_media: 'Marcha média',
  marcha_longa: 'Marcha longa',
} as const;

export const hojeIso = () => new Date().toISOString().slice(0, 10);

/** Classe na data de referencia, contando so os filhos cadastrados. */
export function classeNaData(animal: Animal, todos: readonly Animal[], naData: string): Classe {
  const nascimentosDosFilhos = todos
    .filter((a) => a.sire_id === animal.id || a.dam_id === animal.id)
    .map((a) => a.birth_date);
  return classeDoAnimal({ sex: animal.sex, birthDate: animal.birth_date, nascimentosDosFilhos }, naData);
}

export function faixaNaData(animal: Animal, naData: string): FaixaDoPotro | null {
  return faixaDoPotro(idadeEmMeses(animal.birth_date, naData));
}

export function formatarIdade(meses: number): string {
  if (meses < 24) return `${meses} ${meses === 1 ? 'mês' : 'meses'}`;
  const anos = Math.floor(meses / 12);
  const resto = meses % 12;
  return resto === 0 ? `${anos} anos` : `${anos} anos e ${resto} ${resto === 1 ? 'mês' : 'meses'}`;
}

export function formatarData(iso: string): string {
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
}

export function formatarValor(valor: number | null, trait: Trait | undefined): string {
  if (valor == null) return '—';
  const casas = trait?.decimals ?? 1;
  const texto = valor.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
  if (trait?.unit === 'escore') return valor > 0 ? `+${texto}` : texto;
  return texto;
}

export const unidadeCurta = (t: Trait | undefined) =>
  t?.unit === 'graus' ? '°' : t?.unit === 'escore' ? '' : t ? ` ${t.unit}` : '';

/**
 * Chave de variavel para comparacao: o codigo, ou CODIGO_E / CODIGO_D quando o
 * trait tem lado — a mesma convencao de `v_measurements_wide`.
 */
export const chaveDaMedida = (m: Pick<Medida, 'trait_code' | 'side'>) =>
  m.side === 'NA' ? m.trait_code : `${m.trait_code}_${m.side}`;

/** Uma ficha achatada em { chave: valor }, pronta para comparar e para os indices. */
export function valoresDaFicha(medidas: readonly Medida[]): Record<string, number | null> {
  const r: Record<string, number | null> = {};
  for (const m of medidas) r[chaveDaMedida(m)] = m.not_applicable ? null : m.value_num;
  return r;
}

/** Cada ficha achatada, por id da avaliacao. */
export function valoresPorFicha(medidas: readonly Medida[]): Map<string, Record<string, number | null>> {
  const porFicha = new Map<string, Medida[]>();
  for (const m of medidas) porFicha.set(m.assessment_id, [...(porFicha.get(m.assessment_id) ?? []), m]);
  return new Map([...porFicha].map(([id, ms]) => [id, valoresDaFicha(ms)]));
}

/**
 * A populacao de uma medida para a curva de referencia: (idade, valor) de cada
 * ficha dos OUTROS animais — o proprio animal nao entra na regua com que e medido.
 */
export function curvaDaPopulacao(
  codigo: string,
  animais: readonly Animal[],
  avaliacoes: readonly Avaliacao[],
  valores: ReadonlyMap<string, Record<string, number | null>>,
  excetoAnimalId?: string,
): CurvaDeReferencia {
  const nascimento = new Map(animais.map((a) => [a.id, a.birth_date]));
  const pontos = avaliacoes.flatMap((av) => {
    const valor = valores.get(av.id)?.[codigo];
    const nasc = nascimento.get(av.animal_id);
    if (av.animal_id === excetoAnimalId || valor == null || !nasc) return [];
    return [{ meses: idadeEmMeses(nasc, av.visit_date), valor }];
  });
  return curvaDeReferencia(pontos);
}

export const ROTULO_LEITURA = { abaixo: 'abaixo do esperado', dentro: 'dentro do esperado', acima: 'acima do esperado' } as const;

export const ROTULO_INDICE = {
  garupaCernelha: 'Garupa / cernelha',
  corporal: 'Corporal',
  toracico: 'Torácico',
  ossatura: 'Ossatura',
  profundidade: 'Profundidade',
} as const;

export function indicesDaFicha(valores: Record<string, number | null>) {
  const m: MedidasBrutas = {
    A_CER: valores.A_CER ?? null,
    A_GAR: valores.A_GAR ?? null,
    C_COR: valores.C_COR ?? null,
    P_TOR: valores.P_TOR ?? null,
    PE_TOR: valores.PE_TOR ?? null,
    PE_CAN: valores.PE_CAN ?? null,
  };
  return indices(m);
}

export const ROTULO_VISTA = {
  perfil_esquerdo: 'Perfil esquerdo',
  frente: 'De frente',
  tras: 'Por trás',
  pista: 'Pista',
} as const;

/** Titulo curto de uma midia: "Foto de perfil", "De frente · Marcha média"... */
export function tituloDaMidia(m: Pick<Midia, 'kind' | 'view' | 'gait'>): string {
  if (m.kind === 'foto') return m.view === 'perfil_esquerdo' || !m.view ? 'Foto de perfil' : `Foto ${ROTULO_VISTA[m.view].toLowerCase()}`;
  const vista = m.view ? ROTULO_VISTA[m.view] : 'Vídeo';
  return m.gait ? `${vista} · ${ROTULO_ANDAMENTO[m.gait]}` : vista;
}

const normalizar = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase('pt-BR');

/** Busca por nome ou registro, sem diferenciar acento e maiuscula ("perola" acha "Pérola"). */
export function casaBusca(animal: Pick<Animal, 'name' | 'abccmm_registry'>, termo: string): boolean {
  const t = normalizar(termo.trim());
  return !t || normalizar(animal.name).includes(t) || normalizar(animal.abccmm_registry ?? '').includes(t);
}

/** Nascimento para exibir: data aproximada aparece so com o ano, marcada com ≈. */
export function formatarNascimento(a: Pick<Animal, 'birth_date' | 'birth_date_estimated'>): string {
  return a.birth_date_estimated ? `≈ ${a.birth_date.slice(0, 4)}` : formatarData(a.birth_date);
}

/** Idade na data; com nascimento aproximado, marcada com ≈. */
export function idadeDoAnimal(a: Pick<Animal, 'birth_date' | 'birth_date_estimated'>, naData: string): string {
  const texto = formatarIdade(idadeEmMeses(a.birth_date, naData));
  return a.birth_date_estimated ? `≈ ${texto}` : texto;
}

export const ROTULO_COMPARTILHAMENTO = {
  privado: 'Privado',
  agregado: 'Agregado',
  publico: 'Público',
} as const;

export const PELAGENS = [
  'Alazão', 'Baio', 'Castanho', 'Lobuno', 'Pampa', 'Preto', 'Rosilho', 'Tordilho',
] as const;
